import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Check your inbox for a reset link');
    } catch (err) {
      // Endpoint always returns 200, but handle network errors
      toast.error('Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium mb-8"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="Growing Minds" className="h-14 w-auto" onError={(e)=>{e.currentTarget.style.display='none';}} />
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Forgot password?</h1>
            <p className="text-slate-500">We'll email you a reset link</p>
          </div>
        </div>

        {submitted ? (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 text-center" data-testid="forgot-password-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-bold text-slate-800 text-lg mb-2">Check your email</h2>
            <p className="text-slate-600 text-sm mb-4">
              If an account exists for <span className="font-semibold">{email}</span>, you'll receive a reset link shortly.
              The link is valid for 24 hours.
            </p>
            <p className="text-xs text-slate-500">
              For student accounts, the email is sent to the linked parent.
            </p>
            <Link to="/login">
              <Button className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="back-to-login-button">
                Back to login
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email or username</Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="parent@example.com or firstname.surname"
                className="h-12 border-slate-200"
                required
                data-testid="forgot-password-email"
              />
              <p className="text-xs text-slate-500 flex items-start gap-1.5 pt-1">
                <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Students should enter their username — the reset link will be emailed to the parent on file.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-semibold"
              data-testid="forgot-password-submit"
            >
              {loading ? 'Sending...' : 'Send reset link'}
            </Button>

            <p className="text-center text-sm text-slate-600">
              Remembered it?{' '}
              <Link to="/login" className="text-emerald-600 font-semibold hover:underline">
                Back to login
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
